import React, { useEffect, useRef, useState } from 'react'
import { useIntl } from 'react-intl'

import Select from 'components/Form/Select'
import FormattedMessage from 'components/FormattedMessage'

import Pagination from './Pagination'
import Placeholder from './Placeholder'

import { ProjectsDropdownProps } from '../types'
import { usePreferredPartnersForProjectsDropdownQuery } from '../graphql.generated'

import useOutsideClickHandler from 'hooks/outsideClickHandler'
import useBool from 'hooks/bool'
import useDebounce from 'hooks/debounce'
import useEventListeners from 'hooks/eventListener'

import { formatErrorMessage } from 'helpers/contactRequestForm'
import classNames from 'helpers/css'

import styles from './index.module.scss'

const DEFAULT_OFFSET = 0
const DEFAULT_QUERY = '*'
const DEFAULT_VALUE = '0'
const QUERY_OUTPUT_LIMIT = 10

const ProjectsDropdown: React.FC<ProjectsDropdownForConsultants> = (props) => {
  const {
    className = '',
    selectDropdownClassName = '',
    withEmptyOptions = false,
    optionWrapper,
    initialValue,
  } = props

  const intl = useIntl()
  const selectDropdownRef = useRef<HTMLDivElement>(null)
  const selectInputRef = useRef<HTMLDivElement>(null)

  const [offset, setOffset] = useState(DEFAULT_OFFSET) // logical offset used in GraphQL query (debounced)
  const [page, setPage] = useState(DEFAULT_OFFSET) // visually presented page in pagination
  const [query, setQuery] = useState(DEFAULT_QUERY)

  const [isSelectOpen, openSelect, closeSelect] = useBool()

  const debouncedSearch = useDebounce((input) => onSearchInput(input as string))
  const debouncedPagination = useDebounce((page) => setOffset(page as number), 400)

  const defaultIncludeProjectId = initialValue === DEFAULT_VALUE ? '' : initialValue?.toString()

  const {
    loading,
    data,
  } = usePreferredPartnersForProjectsDropdownQuery({
    variables: {
      limit: QUERY_OUTPUT_LIMIT,
      offset: offset * QUERY_OUTPUT_LIMIT,
      query,
      includeProjectId: initialValue ? defaultIncludeProjectId : '',
    },
  })

  const closeSelectDropdown = () => {
    closeSelect()
    setOffset(DEFAULT_OFFSET)
    setPage(DEFAULT_OFFSET)
  }

  const onSearchInput = (value: string = DEFAULT_QUERY) => {
    setOffset(DEFAULT_OFFSET)
    setPage(DEFAULT_OFFSET)
    setQuery(value || DEFAULT_QUERY)
  }

  useOutsideClickHandler([selectInputRef, selectDropdownRef], () => {
    if (isSelectOpen) {
      closeSelectDropdown()
    }
  }, [isSelectOpen])

  useEventListeners({
    scroll: closeSelectDropdown,
  }, [closeSelectDropdown])

  useEffect(() => {
    if (isSelectOpen) {
      setOffset(DEFAULT_OFFSET)
      setPage(DEFAULT_OFFSET)
    }
  }, [isSelectOpen])

  useEffect(() => {
    if (page !== offset) {
      debouncedPagination(page)
    }
  }, [page])

  return (
    <div ref={selectInputRef} onClick={openSelect}>
      <Select
        name="projectId"
        showSearch
        autoClearSearchValue={false}
        filterOption={false}
        defaultActiveFirstOption={false}
        onSearch={debouncedSearch}
        rules={{ required: formatErrorMessage(intl, 'selectEvent') }}
        disabled={loading}
        autoFocus
        className={classNames(styles, 'projects-dropdown__for-consultants', className)}
        open={isSelectOpen}
        onSelect={closeSelect}
        defaultValue={initialValue}
        placeholder={loading ? intl.formatMessage({ id: 'common.loading' }) : null}
        dropdownClassName={selectDropdownClassName}
        notFoundContent={<Placeholder loading={loading} />}
        dropdownRender={menu => (
          <div
            ref={selectDropdownRef}
            className={classNames(styles, 'projects-dropdown__for-consultants__dropdown')}
          >
            {data?.preferredPartners?.length ? menu : <Placeholder loading={loading} />}

            <Pagination page={page} setPage={setPage} />
          </div>
        )}
      >
        {(withEmptyOptions && data?.preferredPartners?.length) && (
          <Select.Option key="newProject" value={DEFAULT_VALUE}>
            <FormattedMessage id="newHeader.navigation.projectSelector.newProject" />
          </Select.Option>
        )}

        {data?.preferredPartners?.map((partner) => partner.children?.length && (
          <Select.OptGroup key={partner.name}>
            {partner.children?.map((project) => (
              <Select.Option key={project.id} value={project.id}>
                {optionWrapper ? optionWrapper(project) : project.title}
              </Select.Option>
            ))}
          </Select.OptGroup>
        ))}
      </Select>
    </div>
  )
}

type ProjectsDropdownForConsultants = ProjectsDropdownProps

export default ProjectsDropdown
